import React, { useState, useEffect, useCallback } from 'react';
import { useLocalization } from '../../hooks/useLocalization';

interface DataItem {
    _id: string;
    [key: string]: any;
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    pages: number;
}

interface Stats {
    totals: { hotels: number; flights: number; offers: number };
    hotelsByCountry: { _id: string; count: number }[];
    priceStats: { avgPrice: number; minPrice: number; maxPrice: number };
}

const API_BASE = '/api/data-explorer';

// Entity tabs will be created inside component for localization

interface LocationOption {
    id: string;
    name: string;
}

const DataExplorer: React.FC = () => {
    const { t, language } = useLocalization();

    // Helper for inline translations
    const L = (en: string, sv: string) => language === 'sv' ? sv : en;

    // Localized entity tabs
    const ENTITY_TABS = [
        { id: 'hotel', label: L('Hotels', 'Hotell'), icon: '🏨' },
        { id: 'flight', label: L('Flights', 'Flyg'), icon: '✈️' },
        { id: 'offer', label: L('Offers', 'Erbjudanden'), icon: '💰' },
        { id: 'cache', label: 'Cache', icon: '💾' }
    ];

    const [activeEntity, setActiveEntity] = useState('hotel');
    const [items, setItems] = useState<DataItem[]>([]);
    const [stats, setStats] = useState<Stats | null>(null);
    const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, pages: 0 });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [search, setSearch] = useState('');
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [detailItem, setDetailItem] = useState<DataItem | null>(null);
    const [editingItem, setEditingItem] = useState<DataItem | null>(null);

    // Advanced filters
    const [showFilters, setShowFilters] = useState(false);
    const [checkInDate, setCheckInDate] = useState('');
    const [checkOutDate, setCheckOutDate] = useState('');
    const [departureCities, setDepartureCities] = useState<LocationOption[]>([]);
    const [selectedDeparture, setSelectedDeparture] = useState('');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [starRating, setStarRating] = useState('');
    const [country, setCountry] = useState('');

    const showMessage = (type: 'success' | 'error', text: string) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 3000);
    };

    // Fetch departure cities from tour-cache API
    const fetchDepartures = useCallback(async () => {
        try {
            const res = await fetch('/api/tour-cache/locations/departures');
            const data = await res.json();
            if (data.success && data.locations) {
                setDepartureCities(data.locations);
            }
        } catch (err) {
            console.error('Failed to fetch departures:', err);
        }
    }, []);

    const fetchStats = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/stats`);
            const data = await res.json();
            if (data.success) setStats(data.stats);
        } catch (err) {
            console.error('Stats fetch error:', err);
        }
    }, []);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString(),
                ...(search && { search }),
                ...(minPrice && { minPrice }),
                ...(maxPrice && { maxPrice }),
                ...(starRating && { stars: starRating }),
                ...(country && { country }),
                ...(checkInDate && { checkIn: checkInDate }),
                ...(checkOutDate && { checkOut: checkOutDate }),
                ...(selectedDeparture && { departure: selectedDeparture })
            });
            const res = await fetch(`${API_BASE}/${activeEntity}?${params}`);
            const data = await res.json();
            if (data.success) {
                setItems(data.items);
                setPagination(data.pagination);
            }
        } catch (err) {
            showMessage('error', L('Failed to load data', 'Det gick inte att läsa in data'));
        }
        setLoading(false);
    }, [activeEntity, pagination.page, pagination.limit, search, minPrice, maxPrice, starRating, country, checkInDate, checkOutDate, selectedDeparture]);

    const fetchDetail = async (id: string) => {
        try {
            const res = await fetch(`${API_BASE}/${activeEntity}/${id}`);
            const data = await res.json();
            if (data.success) setDetailItem(data.item);
        } catch (err) {
            showMessage('error', L('Failed to load details', 'Det gick inte att läsa in detaljer'));
        }
    };

    const updateItem = async () => {
        if (!editingItem) return;
        try {
            const res = await fetch(`${API_BASE}/${activeEntity}/${editingItem._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editingItem)
            });
            const data = await res.json();
            if (data.success) {
                showMessage('success', L('Updated', 'Uppdaterad'));
                setEditingItem(null);
                fetchItems();
            }
        } catch (err) {
            showMessage('error', L('Update failed', 'Uppdatering misslyckades'));
        }
    };

    const deleteItem = async (id: string) => {
        if (!confirm(L('Are you sure you want to delete this record?', 'Är du säker på att du vill radera denna post?'))) return;
        try {
            const res = await fetch(`${API_BASE}/${activeEntity}/${id}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                showMessage('success', L('Deleted', 'Raderad'));
                fetchItems();
            }
        } catch (err) {
            showMessage('error', L('Delete failed', 'Radering misslyckades'));
        }
    };

    const bulkDelete = async () => {
        if (selectedItems.length === 0) return;
        if (!confirm(L(`${selectedItems.length} records will be deleted. Are you sure?`, `${selectedItems.length} poster kommer att raderas. Är du säker?`))) return;
        try {
            const res = await fetch(`${API_BASE}/${activeEntity}/bulk-delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: selectedItems })
            });
            const data = await res.json();
            if (data.success) {
                showMessage('success', L(`${data.deleted} records deleted`, `${data.deleted} poster raderade`));
                setSelectedItems([]);
                fetchItems();
                fetchStats();
            }
        } catch (err) {
            showMessage('error', L('Bulk delete failed', 'Massradering misslyckades'));
        }
    };

    const bulkStatus = async (isActive: boolean) => {
        if (selectedItems.length === 0) return;
        try {
            const res = await fetch(`${API_BASE}/${activeEntity}/bulk-status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: selectedItems, isActive })
            });
            const data = await res.json();
            if (data.success) {
                showMessage('success', L(`${data.modified} records updated`, `${data.modified} poster uppdaterade`));
                setSelectedItems([]);
                fetchItems();
            }
        } catch (err) {
            showMessage('error', L('Update failed', 'Uppdatering misslyckades'));
        }
    };

    const exportData = async (format: 'json' | 'csv') => {
        window.open(`${API_BASE}/${activeEntity}/export/${format}`, '_blank');
    };

    useEffect(() => {
        fetchStats();
        fetchDepartures();
    }, [fetchStats, fetchDepartures]);

    useEffect(() => {
        fetchItems();
        setSelectedItems([]);
    }, [activeEntity, pagination.page, search, minPrice, maxPrice, starRating, country, checkInDate, checkOutDate, selectedDeparture]);

    const toggleSelect = (id: string) => {
        setSelectedItems(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedItems.length === items.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(items.map(i => i._id));
        }
    };

    const getDisplayValue = (item: DataItem, field: string) => {
        const parts = field.split('.');
        let value: any = item;
        for (const part of parts) {
            if (value == null) return '-';
            value = value[part];
        }
        return value ?? '-';
    };

    const renderItemPreview = (item: DataItem) => {
        switch (activeEntity) {
            case 'hotel':
                return (
                    <div className="flex items-center gap-3">
                        {item.images?.[0]?.url && (
                            <img src={item.images[0].url} alt="" className="w-12 h-12 rounded object-cover" />
                        )}
                        <div>
                            <div className="font-medium">{item.name}</div>
                            <div className="text-xs text-gray-500">
                                {'⭐'.repeat(item.stars || 0)} • {item.location?.name || item.location?.city}
                            </div>
                        </div>
                    </div>
                );
            case 'flight':
                return (
                    <div>
                        <div className="font-medium">{item.airline?.name} - {item.flightNumber}</div>
                        <div className="text-xs text-gray-500">
                            {item.departure?.airport?.code} → {item.arrival?.airport?.code}
                        </div>
                    </div>
                );
            case 'offer':
                return (
                    <div>
                        <div className="font-medium">€{item.price?.amount?.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">
                            {item.room?.name} • {item.boardType?.name} • {item.nights} gece
                        </div>
                    </div>
                );
            case 'cache':
                return (
                    <div>
                        <div className="font-medium">{item.queryId || item.searchParams?.destination || 'Cache Verisi'}</div>
                        <div className="text-xs text-gray-500">
                            🏨 {item.hotels?.length || 0} otel • {item.status || 'completed'}
                        </div>
                    </div>
                );
            default:
                return <div>{item.name || item._id}</div>;
        }
    };

    return (
        <div className="space-y-4">
            {/* Message */}
            {message && (
                <div className={`px-4 py-2 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {message.text}
                </div>
            )}

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-4">
                        <div className="text-3xl font-bold">{stats.totals.hotels}</div>
                        <div className="text-sm opacity-80">🏨 {L('Hotels', 'Hotell')}</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-4">
                        <div className="text-3xl font-bold">{stats.totals.flights}</div>
                        <div className="text-sm opacity-80">✈️ {L('Flights', 'Flyg')}</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-4">
                        <div className="text-3xl font-bold">{stats.totals.offers}</div>
                        <div className="text-sm opacity-80">💰 {L('Offers', 'Erbjudanden')}</div>
                    </div>
                    <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl p-4">
                        <div className="text-3xl font-bold">€{stats.priceStats.avgPrice?.toFixed(0) || 0}</div>
                        <div className="text-sm opacity-80">📊 {L('Avg Price', 'Snittpris')}</div>
                    </div>
                </div>
            )}

            {/* Main Panel */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold">📊 {L('Data Explorer', 'Datautforskare')}</h2>
                        <div className="flex gap-2">
                            <button
                                onClick={() => exportData('json')}
                                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded text-sm"
                            >
                                📥 JSON
                            </button>
                            <button
                                onClick={() => exportData('csv')}
                                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded text-sm"
                            >
                                📥 CSV
                            </button>
                        </div>
                    </div>

                    {/* Entity Tabs */}
                    <div className="flex gap-2 mt-4">
                        {ENTITY_TABS.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => { setActiveEntity(tab.id); setPagination(p => ({ ...p, page: 1 })); }}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeEntity === tab.id
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {tab.icon} {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Toolbar */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex gap-4 items-center mb-3">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
                            placeholder={`🔍 ${L('Search...', 'Sök...')}`}
                            className="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                        />
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${showFilters ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200'}`}
                        >
                            🎛️ {L('Filters', 'Filter')} {showFilters ? '▲' : '▼'}
                        </button>

                        {selectedItems.length > 0 && (
                            <div className="flex gap-2">
                                <span className="text-sm text-gray-500">{selectedItems.length} {L('selected', 'valda')}</span>
                                <button
                                    onClick={() => bulkStatus(true)}
                                    className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm"
                                >
                                    ✓ {L('Active', 'Aktiv')}
                                </button>
                                <button
                                    onClick={() => bulkStatus(false)}
                                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                                >
                                    ✗ {L('Inactive', 'Inaktiv')}
                                </button>
                                <button
                                    onClick={bulkDelete}
                                    className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm"
                                >
                                    🗑️ {L('Delete', 'Radera')}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Advanced Filters Panel */}
                    {showFilters && (
                        <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                {/* Date Range */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">📅 {L('Check-in', 'Incheckning')}</label>
                                    <input
                                        type="date"
                                        value={checkInDate}
                                        onChange={(e) => setCheckInDate(e.target.value)}
                                        className="w-full px-2 py-1.5 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">📅 {L('Check-out', 'Utcheckning')}</label>
                                    <input
                                        type="date"
                                        value={checkOutDate}
                                        onChange={(e) => setCheckOutDate(e.target.value)}
                                        className="w-full px-2 py-1.5 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600"
                                    />
                                </div>

                                {/* Departure City */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">✈️ {L('Departure City', 'Avgångsstad')}</label>
                                    <select
                                        value={selectedDeparture}
                                        onChange={(e) => setSelectedDeparture(e.target.value)}
                                        className="w-full px-2 py-1.5 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600 bg-white"
                                    >
                                        <option value="">{L('All', 'Alla')}</option>
                                        {departureCities.map(city => (
                                            <option key={city.id} value={city.id}>{city.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Price Range */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">💰 {L('Min Price', 'Min Pris')} (€)</label>
                                    <input
                                        type="number"
                                        value={minPrice}
                                        onChange={(e) => setMinPrice(e.target.value)}
                                        placeholder="0"
                                        className="w-full px-2 py-1.5 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">💰 {L('Max Price', 'Max Pris')} (€)</label>
                                    <input
                                        type="number"
                                        value={maxPrice}
                                        onChange={(e) => setMaxPrice(e.target.value)}
                                        placeholder="10000"
                                        className="w-full px-2 py-1.5 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600"
                                    />
                                </div>

                                {/* Star Rating */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">⭐ {L('Stars', 'Stjärnor')}</label>
                                    <select
                                        value={starRating}
                                        onChange={(e) => setStarRating(e.target.value)}
                                        className="w-full px-2 py-1.5 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600 bg-white"
                                    >
                                        <option value="">{L('All', 'Alla')}</option>
                                        <option value="5">⭐⭐⭐⭐⭐ (5)</option>
                                        <option value="4">⭐⭐⭐⭐ (4)</option>
                                        <option value="3">⭐⭐⭐ (3)</option>
                                        <option value="2">⭐⭐ (2)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Filter Actions */}
                            <div className="flex gap-2 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <button
                                    onClick={() => {
                                        setCheckInDate('');
                                        setCheckOutDate('');
                                        setSelectedDeparture('');
                                        setMinPrice('');
                                        setMaxPrice('');
                                        setStarRating('');
                                        setCountry('');
                                    }}
                                    className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg text-sm font-medium"
                                >
                                    🔄 {L('Clear Filters', 'Rensa filter')}
                                </button>
                                <button
                                    onClick={() => fetchItems()}
                                    className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm font-medium"
                                >
                                    🔍 {L('Apply', 'Tillämpa')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="text-center py-12 text-gray-500">{L('Loading...', 'Laddar...')}</div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <div className="text-4xl mb-3">📭</div>
                            <div>{L('No data found', 'Ingen data hittades')}</div>
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="w-10 p-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedItems.length === items.length && items.length > 0}
                                            onChange={toggleSelectAll}
                                        />
                                    </th>
                                    <th className="text-left p-3 text-sm font-medium">{L('Preview', 'Förhandsvisning')}</th>
                                    <th className="text-left p-3 text-sm font-medium">ID</th>
                                    <th className="text-left p-3 text-sm font-medium">{L('Status', 'Status')}</th>
                                    <th className="text-left p-3 text-sm font-medium">{L('Date', 'Datum')}</th>
                                    <th className="w-24 p-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(item => (
                                    <tr
                                        key={item._id}
                                        className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                        <td className="p-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedItems.includes(item._id)}
                                                onChange={() => toggleSelect(item._id)}
                                            />
                                        </td>
                                        <td className="p-3">{renderItemPreview(item)}</td>
                                        <td className="p-3 text-xs font-mono text-gray-500">{item.tourvisioId || item._id}</td>
                                        <td className="p-3">
                                            <span className={`px-2 py-1 rounded text-xs ${item.isActive !== false
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-gray-100 text-gray-500'
                                                }`}>
                                                {item.isActive !== false ? L('Active', 'Aktiv') : L('Inactive', 'Inaktiv')}
                                            </span>
                                        </td>
                                        <td className="p-3 text-xs text-gray-500">
                                            {new Date(item.createdAt || item.lastUpdated).toLocaleDateString('tr-TR')}
                                        </td>
                                        <td className="p-3">
                                            <div className="flex gap-1 justify-end">
                                                <button
                                                    onClick={() => fetchDetail(item._id)}
                                                    className="p-1 hover:bg-gray-200 rounded text-sm"
                                                    title="Detay"
                                                >
                                                    👁️
                                                </button>
                                                <button
                                                    onClick={() => setEditingItem(item)}
                                                    className="p-1 hover:bg-gray-200 rounded text-sm"
                                                    title="Düzenle"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    onClick={() => deleteItem(item._id)}
                                                    className="p-1 hover:bg-red-100 text-red-500 rounded text-sm"
                                                    title="Sil"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                        <div className="text-sm text-gray-500">
                            {L('Total', 'Totalt')} {pagination.total} {L('records', 'poster')}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                                disabled={pagination.page <= 1}
                                className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm disabled:opacity-50"
                            >
                                ←
                            </button>
                            <span className="px-3 py-1 text-sm">{pagination.page} / {pagination.pages}</span>
                            <button
                                onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                                disabled={pagination.page >= pagination.pages}
                                className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm disabled:opacity-50"
                            >
                                →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {detailItem && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-[600px] max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold">📋 {L('Details', 'Detaljer')}</h3>
                            <button onClick={() => setDetailItem(null)} className="text-xl">✕</button>
                        </div>
                        <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-lg text-xs overflow-x-auto">
                            {JSON.stringify(detailItem, null, 2)}
                        </pre>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {editingItem && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-[600px] max-h-[80vh] overflow-y-auto">
                        <h3 className="text-lg font-bold mb-4">✏️ {L('Edit', 'Redigera')}</h3>

                        <div className="space-y-3">
                            {activeEntity === 'hotel' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">{L('Hotel Name', 'Hotellnamn')}</label>
                                        <input
                                            type="text"
                                            value={editingItem.name || ''}
                                            onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                                            className="w-full px-3 py-2 border rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">{L('Stars', 'Stjärnor')}</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="5"
                                            value={editingItem.stars || ''}
                                            onChange={(e) => setEditingItem({ ...editingItem, stars: parseInt(e.target.value) })}
                                            className="w-full px-3 py-2 border rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">{L('Description', 'Beskrivning')}</label>
                                        <textarea
                                            value={editingItem.description || ''}
                                            onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                                            className="w-full px-3 py-2 border rounded-lg"
                                            rows={4}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={editingItem.isActive !== false}
                                    onChange={(e) => setEditingItem({ ...editingItem, isActive: e.target.checked })}
                                />
                                <label className="text-sm">{L('Active', 'Aktiv')}</label>
                            </div>
                        </div>

                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={() => setEditingItem(null)}
                                className="flex-1 px-4 py-2 border rounded-lg"
                            >
                                {L('Cancel', 'Avbryt')}
                            </button>
                            <button
                                onClick={updateItem}
                                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg font-bold"
                            >
                                {L('Save', 'Spara')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataExplorer;
